import React, { useEffect, useMemo, useState } from 'react';
import ProjectTeamService from '../../services/ProjectTeamService';
import ProjectService from '../../services/ProjectService';
import MemberService from '../../services/MemberService';
import RoleService from '../../services/RoleService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const ProjectTeam = () => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [members, setMembers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterMember, setFilterMember] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        projectId: '',
        memberId: '',
        roleId: '',
        remunerationType: '',
        remunerationAmount: '',
        isActive: 1
    });
    const [bulkProjectId, setBulkProjectId] = useState('');
    const [bulkFormData, setBulkFormData] = useState([{
        memberId: '',
        roleId: '',
        remunerationType: '',
        remunerationAmount: ''
    }]);
    const filteredTeamMembers = useMemo(() => teamMembers.filter((member) => {
        if (!searchText) return true;
        return filterList([member], searchText, ['memberName', 'projectName', 'roleName', 'remunerationType', 'status']).length > 0;
    }), [teamMembers, searchText]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterProject('');
        setFilterMember('');
        setFilterRole('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchDropdownData();
        fetchData();
    }, [currentPage, filterProject, filterMember, filterRole, filterStatus]);

    const fetchDropdownData = async () => {
        try {
            const [projectsRes, membersRes, rolesRes] = await Promise.all([
                ProjectService.getAll(1, 100), // Get all projects for dropdown
                MemberService.getAll(1, 100), // Get all members
                RoleService.getAll(1, 100) // Get all roles
            ]);
            setProjects(projectsRes.data || []);
            setMembers(membersRes.data || []);
            setRoles(rolesRes.data || []);
        } catch (err) {
            utility.showError('Failed to load dropdown data: ' + err.message);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await ProjectTeamService.getAll(currentPage, itemsPerPage, {
                projectId: filterProject,
                memberId: filterMember,
                roleId: filterRole,
                status: filterStatus
            });
            setTeamMembers(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (teamMember = null) => {
        if (teamMember) {
            setEditMode(true);
            setFormData({
                id: teamMember.id,
                projectId: teamMember.projectId,
                memberId: teamMember.memberId || '',
                roleId: teamMember.roleId || '',
                remunerationType: teamMember.remunerationType || '',
                remunerationAmount: teamMember.remunerationAmount || '',
                isActive: teamMember.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                projectId: '',
                memberId: '',
                roleId: '',
                remunerationType: '',
                remunerationAmount: '',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            id: '',
            projectId: '',
            memberId: '',
            roleId: '',
            remunerationType: '',
            remunerationAmount: '',
            isActive: 1
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await ProjectTeamService.update(formData.id, formData);
                utility.showSuccess('Project team member updated successfully');
            } else {
                await ProjectTeamService.create(formData);
                utility.showSuccess('Project team member created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };

    const handleBulkOpenModal = () => {
        setBulkProjectId('');
        setBulkFormData([{
            memberId: '',
            roleId: '',
            remunerationType: '',
            remunerationAmount: ''
        }]);
        setShowBulkModal(true);
    };

    const handleBulkCloseModal = () => {
        setShowBulkModal(false);
        setBulkProjectId('');
        setBulkFormData([{
            memberId: '',
            roleId: '',
            remunerationType: '',
            remunerationAmount: ''
        }]);
    };

    const handleBulkInputChange = (index, field, value) => {
        const updatedBulkData = [...bulkFormData];
        updatedBulkData[index][field] = value;
        setBulkFormData(updatedBulkData);
    };

    const handleAddBulkRow = () => {
        setBulkFormData([...bulkFormData, {
            memberId: '',
            roleId: '',
            remunerationType: '',
            remunerationAmount: ''
        }]);
    };

    const handleRemoveBulkRow = (index) => {
        if (bulkFormData.length > 1) {
            setBulkFormData(bulkFormData.filter((_, i) => i !== index));
        }
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const payload = bulkFormData.map(member => ({ ...member, projectId: bulkProjectId }));
            await ProjectTeamService.createBulk(payload);
            utility.showSuccess(`${bulkFormData.length} team members added successfully`);
            handleBulkCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0 me-2" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add Team Member
                    </button>
                    <button className="btn bg-gradient-info mb-0" onClick={handleBulkOpenModal}>
                        <i className="material-symbols-rounded text-sm">playlist_add</i>&nbsp;&nbsp;Add Multiple
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Project Team</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-md-4">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search project team members..."
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Project</label>
                                            <select className="form-select" value={filterProject} onChange={handleFilterChange(setFilterProject)}>
                                                <option value="">All Projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Member</label>
                                            <select className="form-select" value={filterMember} onChange={handleFilterChange(setFilterMember)}>
                                                <option value="">All Members</option>
                                                {members.map((member) => (
                                                    <option key={member.id} value={member.id}>{member.memberName || member.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Role</label>
                                            <select className="form-select" value={filterRole} onChange={handleFilterChange(setFilterRole)}>
                                                <option value="">All Roles</option>
                                                {roles.map((role) => (
                                                    <option key={role.id} value={role.id}>{role.roleName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">&nbsp;</label>
                                            <button type="button" className="mt-1 btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Project</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Member</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Role</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Remuneration</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTeamMembers.length === 0 ? (
                                                <tr><td colSpan="6" className="text-center py-4">No team members found.</td></tr>
                                            ) : (
                                                filteredTeamMembers.map((member) => (
                                                    <tr key={member.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{member.projectName || 'Unknown'}</h6>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{member.memberName || member.teamMemberName || 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{member.roleName || member.role || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle">
                                                            <p className="text-xs font-weight-bold mb-0">
                                                                {member.remunerationType || 'N/A'} - ₹{member.remunerationAmount || 0}
                                                            </p>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${member.isActive ? 'success' : 'secondary'}`}
                                                            >
                                                                {member.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(member)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    <ReactPaginate
                                                        previousLabel="<"
                                                        nextLabel=">"
                                                        breakLabel="..."
                                                        pageCount={pageCount}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        onPageChange={handlePageClick}
                                                        forcePage={currentPage - 1}
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal */}
                {showModal && (
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title font-weight-normal">
                                        {editMode ? 'Edit Team Member' : 'Add Team Member'}
                                    </h5>
                                    <button type="button" className="btn-close text-dark" aria-label="Close" onClick={handleCloseModal} />
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body row">
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Project *</label>
                                                <select
                                                    name="projectId"
                                                    className="form-control"
                                                    value={formData.projectId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Project</option>
                                                    {projects.map(project => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Member *</label>
                                                <select
                                                    name="memberId"
                                                    className="form-control"
                                                    value={formData.memberId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Member</option>
                                                    {members.map(member => (
                                                        <option key={member.id} value={member.id}>
                                                            {member.memberName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Role *</label>
                                                <select
                                                    name="roleId"
                                                    className="form-control"
                                                    value={formData.roleId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Role</option>
                                                    {roles.map(role => (
                                                        <option key={role.id} value={role.id}>
                                                            {role.roleName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Remuneration Type</label>
                                                <select
                                                    name="remunerationType"
                                                    className="form-control"
                                                    value={formData.remunerationType}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Type</option>
                                                    <option value="Daily">Daily</option>
                                                    <option value="Monthly">Monthly</option>
                                                    <option value="Fixed">Fixed</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Remuneration Amount</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    name="remunerationAmount"
                                                    className="form-control"
                                                    value={formData.remunerationAmount}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter amount"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Is Active</label>
                                                <select
                                                    name="isActive"
                                                    className="form-control"
                                                    value={formData.isActive}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value={1}>Active</option>
                                                    <option value={0}>Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                        <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {editMode ? 'Updating...' : 'Saving...'}
                                                </>
                                            ) : (
                                                editMode ? 'Update' : 'Save'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bulk Modal */}
                {showBulkModal && (
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title font-weight-normal">Add Multiple Team Members</h5>
                                    <button type="button" className="btn-close text-dark" aria-label="Close" onClick={handleBulkCloseModal} />
                                </div>
                                <form onSubmit={handleBulkSubmit}>
                                    <div className="modal-body">
                                        <div className="col-md-12 mb-3">
                                            <div className="input-group input-group-static">
                                                <label>Project *</label>
                                                <select
                                                    name="bulkProjectId"
                                                    className="form-control"
                                                    value={bulkProjectId}
                                                    onChange={(e) => setBulkProjectId(e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select Project</option>
                                                    {projects.map(project => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Member</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Role</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Remuneration Type</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder">Amount</th>
                                                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bulkFormData.map((row, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.memberId}
                                                                    onChange={(e) => handleBulkInputChange(index, 'memberId', e.target.value)}
                                                                    required
                                                                >
                                                                    <option value="">Select</option>
                                                                    {members.map(member => (
                                                                        <option key={member.id} value={member.id}>
                                                                            {member.memberName || member.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={row.roleId}
                                                                    onChange={(e) => handleBulkInputChange(index, 'roleId', e.target.value)}
                                                                >
                                                                    <option value="">Select</option>
                                                                    {roles.map(role => (
                                                                        <option key={role.id} value={role.id}>
                                                                            {role.roleName}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>

                                                                <select
                                                                    name="remunerationType"
                                                                    className="form-select form-select-sm"
                                                                    value={row.remunerationType}
                                                                    onChange={(e) => handleBulkInputChange(index, 'remunerationType', e.target.value)}
                                                                >
                                                                    <option value="">Select Type</option>
                                                                    <option value="Daily">Daily</option>
                                                                    <option value="Monthly">Monthly</option>
                                                                    <option value="Fixed">Fixed</option>
                                                                </select>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Amount"
                                                                    value={row.remunerationAmount}
                                                                    onChange={(e) => handleBulkInputChange(index, 'remunerationAmount', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link text-danger mb-0 p-0"
                                                                    onClick={() => handleRemoveBulkRow(index)}
                                                                    disabled={bulkFormData.length === 1}
                                                                >
                                                                    <i className="material-symbols-rounded">delete</i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary mt-3"
                                            onClick={handleAddBulkRow}
                                        >
                                            <i className="material-symbols-rounded text-sm">add_circle</i>&nbsp;Add Row
                                        </button>
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button type="button" className="btn bg-gradient-secondary" onClick={handleBulkCloseModal} disabled={saving}>Cancel</button>
                                        <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save All'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectTeam;